"use client";

import * as React from "react";
import { AnimatePresence, motion } from "framer-motion";
import clsx from "clsx";
import { createPortal } from "react-dom";

type CustomModalProps = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  maxHeight?: string;
  closeOnOutsideClick?: boolean;
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  bodyClassName?: string;
  maxWidth?:
    | "sm"
    | "md"
    | "lg"
    | "xl"
    | "2xl"
    | "3xl"
    | "4xl"
    | "5xl"
    | "6xl"
    | "7xl"
    | string;
  top?: string;
  centered?: boolean;
};

function resolveMaxWidthClass(maxWidth: CustomModalProps["maxWidth"]) {
  if (!maxWidth) return "max-w-5xl";
  if (maxWidth.startsWith("max-w-")) return maxWidth;
  if (maxWidth.startsWith("[")) return `max-w-${maxWidth}`;
  return `max-w-${maxWidth}`;
}

export function CustomModal({
  open,
  onOpenChange,
  maxHeight = "80vh",
  closeOnOutsideClick = true,
  children,
  footer,
  className,
  bodyClassName,
  maxWidth = "5xl",
  top = "top-[5vh]",
  centered = false,
}: CustomModalProps) {
  const [mounted, setMounted] = React.useState(false);
  const dialogRef = React.useRef<HTMLDivElement | null>(null);
  const previousActiveElementRef = React.useRef<HTMLElement | null>(null);
  const onOpenChangeRef = React.useRef(onOpenChange);

  React.useEffect(() => {
    onOpenChangeRef.current = onOpenChange;
  }, [onOpenChange]);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  React.useEffect(() => {
    if (!open) return;

    previousActiveElementRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onOpenChangeRef.current(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.setTimeout(() => {
      dialogRef.current?.focus();
    }, 0);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
      previousActiveElementRef.current?.focus?.();
    };
  }, [open]);

  if (!mounted) return null;

  return createPortal(
    <AnimatePresence>
      {open ? (
        <div className="fixed inset-0 z-80">
          <motion.button
            type="button"
            aria-label="Close"
            className="absolute inset-0 h-full w-full bg-black/50 backdrop-blur-[2px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={() => {
              if (closeOnOutsideClick) onOpenChangeRef.current(false);
            }}
          />

          <div
            className={clsx(
              "absolute inset-x-4 mx-auto flex justify-center",
              centered ? "top-1/2 -translate-y-1/2" : top,
            )}
          >
            <motion.div
              ref={dialogRef}
              role="dialog"
              aria-modal="true"
              tabIndex={-1}
              initial={{ opacity: 0, y: 16, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.98 }}
              transition={{ type: "spring", stiffness: 420, damping: 32 }}
              className={clsx(
                "fixed inset-x-4 z-80 mx-auto w-full rounded-2xl bg-white shadow-2xl outline-none",
                centered ? "top-1/2 -translate-y-1/2" : top,
                resolveMaxWidthClass(maxWidth),
                className,
              )}
              onClick={(event) => event.stopPropagation()}
            >
              <div
                className={clsx("overflow-y-auto overscroll-contain m-4", bodyClassName)}
                style={{ maxHeight }}
              >
                {children}
              </div>

              {footer ? <div className="border-t border-neutral-200 p-4">{footer}</div> : null}
            </motion.div>
          </div>
        </div>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}
